import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'

import { triggerToast } from '@/components/Toast'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Select,
  Spinner,
} from '@/components/ui'
import {
  useCreateDatasetVersion,
  useDatasetVersionList,
} from '@/features/dataset-version/hook'
import { useDebounce } from '@/hooks/useDebounce'
import { ProjectType, ResponseDatasetVersion } from '@/schema'
import { ERROR_CODES, SEARCH_DEBOUNCE_DELAY_MS } from '@/utils/constants'

interface DatasetVersionInputProps {
  value: ResponseDatasetVersion[]
  onChange: (value: ResponseDatasetVersion[] | undefined) => void
  projectId?: string
  projectType?: ProjectType
  disabled?: boolean
  placeholder?: string
}

export const DatasetVersionInput: React.FC<DatasetVersionInputProps> = ({
  value,
  onChange,
  projectId,
  projectType,
  disabled = false,
  placeholder,
}) => {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState<string>('')
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY_MS)

  const isAscendProject = useMemo(() => {
    return projectType === ProjectType.ASCEND_SPARK
  }, [projectType])

  const isStandaloneProject = useMemo(() => {
    return projectType === ProjectType.STANDALONE
  }, [projectType])

  // Fetch dataset versions based on project type
  const shouldFetch = useMemo(() => {
    if (!projectId) return false
    if (isAscendProject || isStandaloneProject) return true
    return false
  }, [projectId, isAscendProject, isStandaloneProject])

  const trimmedSearch = debouncedSearchText.trim()

  const {
    data: { data = [] } = {},
    isLoading,
    isPending,
    isFetching,
  } = useDatasetVersionList({
    search: trimmedSearch,
    filters: {
      projectId: projectId || '',
      provider: isAscendProject ? 'ascend_spark' : 'standalone',
    },
    enabled: shouldFetch && !!projectId,
  })

  const createMutation = useCreateDatasetVersion()

  // Include selected value in the list if it exists
  const allVersions = useMemo(() => {
    let versions = [...data]
    // Remove if value is in the list
    value.forEach((v) => {
      versions = versions.filter((vv) => vv.id !== v.id)
    })

    // Sort alphabetically
    return versions.sort((a, b) => a.name.localeCompare(b.name))
  }, [data, value])

  // Filter versions based on search (Command handles this, but we also filter for API)
  const filteredVersions = useMemo(() => {
    if (!trimmedSearch) return allVersions
    const lowerSearch = trimmedSearch.toLowerCase()
    return allVersions.filter((v) => v.name.toLowerCase().includes(lowerSearch))
  }, [allVersions, trimmedSearch])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [value])

  const handleSelectVersion = useCallback(
    (version: ResponseDatasetVersion) => {
      onChange([...value, version])
      setSearchText('')
      setIsOpen(false)
    },
    [onChange, value]
  )

  const handleCreateAndSelect = useCallback(
    async (name: string) => {
      if (!projectId || !isStandaloneProject) return

      try {
        const response = await createMutation.mutateAsync({
          name: name.trim(),
          project_id: projectId,
          provider: 'standalone',
        })

        if (response.data && response.data.length > 0) {
          const newVersion = response.data[0]
          onChange([...value, newVersion])
          setSearchText('')
          setIsOpen(false)
          triggerToast(
            'success',
            t('deliverableSet.dialog.messages.datasetVersionCreateSuccess')
          )
          setSearchText('')
          setIsOpen(false)
        }
      } catch (error) {
        const axiosError = error as {
          response?: {
            data?: {
              error?: { code?: string; message?: string }
            }
          }
        }

        const errorCode = axiosError?.response?.data?.error?.code
        const errorMessage =
          axiosError?.response?.data?.error?.message ||
          t('deliverableSet.dialog.messages.datasetVersionCreateError')

        if (errorCode === ERROR_CODES.DUPLICATE) {
          triggerToast(
            'error',
            t('deliverableSet.dialog.messages.datasetVersionAlreadyExists')
          )
        } else {
          triggerToast('error', errorMessage)
        }
      }
    },
    [projectId, isStandaloneProject, createMutation, onChange, value, t]
  )

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange(undefined)
      setSearchText('')
      setIsOpen(false)
    },
    [onChange]
  )

  const handleInputValueChange = useCallback((search: string) => {
    setSearchText(search)
    setIsOpen(true)
  }, [])

  const handleBlur = useCallback(() => {
    setSearchText('')
  }, [])

  const shouldShowCreateOption = useMemo(
    () =>
      !isLoading &&
      !data.some((v) => v.name === trimmedSearch) &&
      isStandaloneProject &&
      trimmedSearch.length > 0 &&
      !createMutation.isPending,
    [
      isLoading,
      data,
      isStandaloneProject,
      createMutation.isPending,
      trimmedSearch,
    ]
  )

  const renderCommandContent = useCallback(() => {
    if (isLoading) {
      return (
        <div className="py-1.5 pl-2 pr-8 text-sm text-gray-500">
          {t('common.searching')}...
        </div>
      )
    }

    if (createMutation.isPending) {
      return (
        <div className="flex items-center justify-center p-8">
          <Spinner />
        </div>
      )
    }

    return (
      <>
        {shouldShowCreateOption && (
          <CommandGroup>
            <CommandItem
              value={`create-${trimmedSearch}`}
              onSelect={() => handleCreateAndSelect(trimmedSearch)}
              className="text-blue-600"
            >
              {createMutation.isPending
                ? `${t('common.creating')}...`
                : `${trimmedSearch} (${t('deliverableSet.dialog.createNewDatasetVersion')})`}
            </CommandItem>
          </CommandGroup>
        )}
        {filteredVersions.length > 0 ? (
          <CommandGroup>
            {filteredVersions.map((version) => (
              <CommandItem
                key={version.id || version.name}
                value={version.id}
                onSelect={() => handleSelectVersion(version)}
              >
                {version.name}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        {!isLoading &&
          filteredVersions.length === 0 &&
          !shouldShowCreateOption && (
            <CommandEmpty>
              {t('deliverableSet.dialog.noMatchesFound')}
            </CommandEmpty>
          )}
      </>
    )
  }, [
    isLoading,
    createMutation.isPending,
    shouldShowCreateOption,
    trimmedSearch,
    handleCreateAndSelect,
    filteredVersions,
    handleSelectVersion,
    t,
  ])

  const isDisabledStandalone = useMemo(
    () =>
      ((isPending || isFetching || isLoading) && !searchText.length) ||
      disabled ||
      !projectId,
    [disabled, isFetching, isLoading, isPending, projectId, searchText.length]
  )

  const isDisabledAscend = useMemo(
    () =>
      isPending ||
      isFetching ||
      isLoading ||
      disabled ||
      !projectId ||
      !data.length,
    [data.length, disabled, isFetching, isLoading, isPending, projectId]
  )

  // For Standalone projects, use Command with create option
  if (isStandaloneProject) {
    return (
      <div ref={containerRef} className="relative">
        <Command className="overflow-visible" shouldFilter={false}>
          <CommandInput
            value={searchText || ''}
            onValueChange={handleInputValueChange}
            disabled={isDisabledStandalone}
            clearable={!!searchText}
            onClear={handleClear}
            onBlur={handleBlur}
            placeholder={
              placeholder ||
              t('deliverableSet.dialog.placeholders.selectDatasetVersion')
            }
            onFocus={() => {
              if (projectId) setIsOpen(true)
            }}
          />
          {isOpen && projectId && (
            <CommandList className="absolute z-50 top-10 w-full mt-1 max-h-[300px] overflow-auto rounded-md border border-input bg-white shadow-lg">
              {renderCommandContent()}
            </CommandList>
          )}
        </Command>
      </div>
    )
  }

  // For ASCEND projects, use simple Select
  return (
    <Select
      value=""
      onChange={(val) => {
        if (!val) return
        const version = data.find((v) => v.id === val)
        if (!version) return

        const isExists = value?.some((v) => v.id === val)
        const currentValue = value || []
        if (isExists) {
          onChange(currentValue.filter((v) => v.id !== val))
        } else {
          onChange([...currentValue, version])
        }
      }}
      fullWidth
      placeholder={placeholder}
      options={data.map((version) => {
        const isSelected = value?.some((v) => v.id === version.id)
        return {
          value: version.id,
          label: version.name,
          iconRight: isSelected && <Check className="mt-2 size-4" />,
        }
      })}
      disabled={isDisabledAscend}
    />
  )
}
